/**
 * Intake BE(spnp) `/v1/datasets` 경계.
 *
 * **등록과 노출 토글은 멱등이다.** 등록은 이번 호출이 행을 만들었을 때만 `201` 이고 이미 있으면
 * `200` 으로 기존 행을 준다 — 그때 `display_name` 은 갱신되지 않는다. 두 경우의 안내 문구가
 * 달라야 하므로 이 경계가 본문 비교가 아니라 **상태 코드**로 갈라 `created` 를 낸다.
 *
 * **`latest_probe` 는 늘 온다.** Probe 를 요청한 적이 없으면 값이 `null` 이다. 키가 없으면 계약 오류다.
 *
 * Owns:
 * - 요청 본문 · 쿼리 구성, 응답 모양 확인과 `Dataset` 변환, 201/200 구분.
 *
 * `parseDataset` 은 후보 수락(`api/candidateApi.ts`)도 쓴다 — 같은 응답 모양을 두 번 적지 않는다.
 *
 * Does not own:
 * - Bearer 첨부 · 401/403 세션 무효화 — `apiClient`. 오류 → 사용자 문구 — `feature/datasetFormatters.ts`.
 * - 주소 검증 — `feature/sourceRef.ts`(서버가 정본이고 FE 는 제출 전 같은 규칙을 먼저 본다).
 */
import {
  apiClient,
  IntakeContractError,
  readBoolean,
  readBoundedString,
  readInstantMs,
  readNullableInstantMs,
  readNullableString,
  readRecord,
  readString,
} from "@/shared/http";
import {
  DATASET_FORMATS,
  DATASET_SOURCE_KINDS,
  REGISTERABLE_SOURCE_KIND,
  type Dataset,
  type LatestProbe,
} from "../domain/dataset";
import { PROBE_STATES } from "../domain/probe";

const RESOURCE = "dataset";

export type RegisterDatasetInput = {
  sourceRef: string;
  displayName: string;
};

export type RegisterDatasetOutcome = {
  dataset: Dataset;
  /** 이번 호출이 행을 만들었는지(`201`). `false` 면 이미 있던 행이다(`200`). */
  created: boolean;
};

export async function registerDataset(
  input: RegisterDatasetInput,
): Promise<RegisterDatasetOutcome> {
  const response = await apiClient.post<unknown>("/v1/datasets", {
    source_kind: REGISTERABLE_SOURCE_KIND,
    source_ref: input.sourceRef,
    display_name: input.displayName,
  });

  return { dataset: parseDataset(response.data), created: response.status === 201 };
}

export async function listDatasets(includeHidden: boolean): Promise<readonly Dataset[]> {
  const response = await apiClient.get<unknown>("/v1/datasets", {
    params: { include_hidden: includeHidden },
  });
  const body = readRecord(response.data, RESOURCE, "body");
  if (!Array.isArray(body.datasets)) {
    throw new IntakeContractError(RESOURCE, "datasets");
  }

  return body.datasets.map(parseDataset);
}

export async function getDataset(datasetUuid: string): Promise<Dataset> {
  const response = await apiClient.get<unknown>(`/v1/datasets/${datasetUuid}`);
  return parseDataset(response.data);
}

export async function hideDataset(datasetUuid: string): Promise<Dataset> {
  const response = await apiClient.post<unknown>(`/v1/datasets/${datasetUuid}/hide`);
  return parseDataset(response.data);
}

export async function showDataset(datasetUuid: string): Promise<Dataset> {
  const response = await apiClient.post<unknown>(`/v1/datasets/${datasetUuid}/show`);
  return parseDataset(response.data);
}

export function parseDataset(value: unknown): Dataset {
  const row = readRecord(value, RESOURCE, "row");

  return {
    uuid: readString(row.uuid, RESOURCE, "uuid"),
    sourceKind: readBoundedString(row.source_kind, DATASET_SOURCE_KINDS, RESOURCE, "source_kind"),
    sourceRef: readString(row.source_ref, RESOURCE, "source_ref"),
    displayName: readString(row.display_name, RESOURCE, "display_name"),
    format: readBoundedString(row.format, DATASET_FORMATS, RESOURCE, "format"),
    formatVersion: readNullableString(row.format_version, RESOURCE, "format_version"),
    formatDetail: readNullableString(row.format_detail, RESOURCE, "format_detail"),
    canProbe: readBoolean(row.can_probe, RESOURCE, "can_probe"),
    // null 은 "보인다" 이고 값은 "숨긴 시각" 이다 — 두 상태를 접지 않는다.
    hiddenAtMs: readNullableInstantMs(row.hidden_at, RESOURCE, "hidden_at"),
    createdAtMs: readInstantMs(row.created_at, RESOURCE, "created_at"),
    latestProbe: parseLatestProbe(row.latest_probe),
  };
}

function parseLatestProbe(value: unknown): LatestProbe | null {
  if (value === null) {
    return null;
  }
  const row = readRecord(value, RESOURCE, "latest_probe");
  return {
    uuid: readString(row.uuid, RESOURCE, "latest_probe.uuid"),
    state: readBoundedString(row.state, PROBE_STATES, RESOURCE, "latest_probe.state"),
    failureCode: readNullableString(row.failure_code, RESOURCE, "latest_probe.failure_code"),
  };
}
